import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const COMPLEXITY = [
 { op: 'Access by index', big: 'O(1)', why: 'direct lookup' },
 { op: 'Update by index', big: 'O(1)', why: 'direct write' },
 { op: 'push()', big: 'O(1)', why: 'add at end' },
 { op: 'pop()', big: 'O(1)', why: 'remove from end' },
 { op: 'unshift()', big: 'O(n)', why: 'shift everything right' },
 { op: 'shift()', big: 'O(n)', why: 'shift everything left' },
 { op: 'Search (indexOf/includes)', big: 'O(n)', why: 'may scan all elements' },
];

export const JSArrays: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Arrays</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Ordered collections with index-based access — plus how they&apos;re stored, why some operations are O(n), sparse arrays, mutation vs immutability, and the interview questions built on all of it.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="20 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>A food order contains Pizza, Burger, Pasta, Cold Drink, Fries. Storing each as its own variable works for five items:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const item1 = "Pizza";\nconst item2 = "Burger";\nconst item3 = "Pasta";\n// ...item50000?` }]} defaultLanguage="javascript" id="arr-loose" />
   <Para>At 50,000 items it&apos;s impossible to manage, search, sort, iterate, or update. We need a structure for <Mark>multiple similar values</Mark> — that&apos;s an array.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">An <Mark>array</Mark> is an ordered collection of values stored under a single variable. Each value is an <Mark>element</Mark>, addressed by a zero-based <Mark>index</Mark>.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const items = ["Pizza", "Burger", "Pasta"];` }]} defaultLanguage="javascript" id="arr-definition" />
   <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-400">{`index:  0        1         2\n        Pizza    Burger    Pasta`}</pre>
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Arrays Exist</SectionHeading>
   <Para>They solve one of the most common problems in software — &quot;store multiple similar things&quot; — for products, users, orders, notifications, messages, transactions.</Para>
   <div className="flex flex-wrap gap-2">{['Products', 'Users', 'Orders', 'Notifications', 'Messages', 'Transactions'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>Think of an array as a <Mark>numbered shelf</Mark>: each item has a fixed position (its index), and knowing the number gives instant access.</Para>
   <pre className="my-2 text-[13px] font-mono text-gray-600 dark:text-gray-400">{`shelf\n 0 → Apple\n 1 → Banana\n 2 → Mango\n 3 → Orange`}</pre>
  </section>

  {/* 5. Creating */}
  <section className="mb-12">
   <SectionHeading n={5}>Creating Arrays</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// 1. Array literal (preferred)\nconst fruits = ["Apple", "Banana", "Mango"];\n\n// 2. Empty, then fill\nconst users = [];\nusers.push("Sachin");\n\n// 3. Array constructor (rare)\nconst arr = new Array("Apple", "Banana");` }]} defaultLanguage="javascript" id="arr-create" />
  </section>

  {/* 6. Accessing */}
  <section className="mb-12">
   <SectionHeading n={6}>Accessing &amp; Updating Elements</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana", "Mango"];\n\nconsole.log(fruits[0]); // "Apple"\nconsole.log(fruits[2]); // "Mango"\n\nfruits[1] = "Orange";   // update in place -> ["Apple","Orange","Mango"]` }]} defaultLanguage="javascript" id="arr-access" />
   <Callout tone="info">Both index access and index update are <Code>O(1)</Code> — a direct lookup/write, no scanning.</Callout>
  </section>

  {/* 7. Length */}
  <section className="mb-12">
   <SectionHeading n={7}>Array Length</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana", "Mango"];\nconsole.log(fruits.length); // 3` }]} defaultLanguage="javascript" id="arr-length" />
  </section>

  {/* 8. Internals */}
  <section className="mb-12">
   <SectionHeading n={8}>Internal Structure</SectionHeading>
   <Para>Arrays aren&apos;t magic — they&apos;re <Mark>specialized objects</Mark> whose keys are numeric strings, plus a <Code>length</Code> property:</Para>
   <pre className="my-2 text-[13px] font-mono text-gray-600 dark:text-gray-400">{`array\n ├── 0 → Apple\n ├── 1 → Banana\n ├── 2 → Mango\n └── length → 3`}</pre>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(typeof []);            // "object"\nconsole.log(Array.isArray([]));    // true  (the reliable check)` }]} defaultLanguage="javascript" id="arr-internals" />
   <Callout tone="info">Because <Code>typeof []</Code> is <Code>"object"</Code>, always use <Code>Array.isArray()</Code> to check for an array.</Callout>
  </section>

  {/* 9. push/pop/shift/unshift */}
  <section className="mb-12">
   <SectionHeading n={9}>Add &amp; Remove: push / pop / shift / unshift</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Banana", "Mango"];\n\nfruits.push("Grapes");    // add at end       -> O(1)\nfruits.pop();             // remove from end  -> O(1)\nfruits.unshift("Apple");  // add at start     -> O(n)\nfruits.shift();           // remove from start-> O(n)` }]} defaultLanguage="javascript" id="arr-addremove" />
   <Callout tone="warning" title="Why are shift/unshift O(n)?">Adding or removing at the <Mark>start</Mark> forces every other element to shift its index. End operations (push/pop) touch nothing else, so they&apos;re O(1).</Callout>
  </section>

  {/* 10. Complexity table */}
  <section className="mb-12">
   <SectionHeading n={10}>Time Complexity</SectionHeading>
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Operation', 'Complexity', 'Why'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      {COMPLEXITY.map((r) => (
       <tr key={r.op}>
        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r.op}</td>
        <td className={`px-4 py-3 font-mono font-bold ${r.big === 'O(1)' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>{r.big}</td>
        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.why}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  </section>

  {/* 11. Searching */}
  <section className="mb-12">
   <SectionHeading n={11}>Searching Arrays</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana", "Mango"];\n\nfruits.includes("Banana"); // true\nfruits.indexOf("Mango");   // 2  (-1 if not found)` }]} defaultLanguage="javascript" id="arr-search" />
   <Callout tone="info">Both are <Code>O(n)</Code> — the array may need a full scan.</Callout>
  </section>

  {/* 12. Iterating */}
  <section className="mb-12">
   <SectionHeading n={12}>Iterating Arrays</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// classic for loop\nfor (let i = 0; i < fruits.length; i++) {\n  console.log(fruits[i]);\n}\n\n// for...of (cleaner)\nfor (const fruit of fruits) {\n  console.log(fruit);\n}` }]} defaultLanguage="javascript" id="arr-iterate" />
   <Output>{`Apple\nBanana\nMango`}</Output>
  </section>

  {/* 13. Arrays of objects + nested */}
  <section className="mb-12">
   <SectionHeading n={13}>Arrays of Objects &amp; Nested Arrays</SectionHeading>
   <Para>An array of objects is the most common production shape (API responses, DB rows, tables):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const users = [\n  { id: 1, name: "Sachin" },\n  { id: 2, name: "Rahul" },\n];\nconsole.log(users[0].name); // "Sachin"\n\n// Nested arrays power matrices, grids, game boards:\nconst matrix = [\n  [1, 2, 3],\n  [4, 5, 6],\n];\nconsole.log(matrix[0][1]); // 2` }]} defaultLanguage="javascript" id="arr-aoo" />
  </section>

  {/* 14. Sparse arrays */}
  <section className="mb-12">
   <SectionHeading n={14}>Sparse Arrays</SectionHeading>
   <Para>A lesser-known gotcha: assigning to an index past the end leaves <Mark>holes</Mark>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const arr = [];\narr[5] = "Hello";\n\nconsole.log(arr);        // [ <5 empty items>, "Hello" ]\nconsole.log(arr.length); // 6` }]} defaultLanguage="javascript" id="arr-sparse" />
   <Callout tone="warning">Holes aren&apos;t the same as <Code>undefined</Code> values — some methods skip them, which leads to subtle bugs. Avoid creating sparse arrays accidentally.</Callout>
  </section>

  {/* 15. References */}
  <section className="mb-12">
   <SectionHeading n={15}>Array References</SectionHeading>
   <Para>Arrays are reference types — assignment shares the same array:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const arr1 = ["Apple"];\nconst arr2 = arr1;     // same reference\narr2.push("Mango");\nconsole.log(arr1);     // ["Apple", "Mango"]` }]} defaultLanguage="javascript" id="arr-ref" />
   <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-5">
    <div className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-[13px] font-medium">
     <span className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">arr1</span>
     <span className="text-gray-400">↘</span>
     <span className="px-4 py-2 rounded-lg border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-mono">{`["Apple", "Mango"]`}</span>
     <span className="text-gray-400">↙</span>
     <span className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">arr2</span>
    </div>
   </div>
  </section>

  {/* 16. Shallow & deep copy */}
  <section className="mb-12">
   <SectionHeading n={16}>Shallow vs Deep Copy</SectionHeading>
   <Para>Spread or <Code>slice()</Code> make an independent top-level copy:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const copy = [...arr1];      // or arr1.slice()\ncopy.push("Orange");          // arr1 untouched` }]} defaultLanguage="javascript" id="arr-shallow" />
   <Para>But with an array of objects, the nested objects are still shared:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const users = [{ name: "Sachin" }];\nconst copy = [...users];          // shallow\ncopy[0].name = "Rahul";\nconsole.log(users[0].name);       // "Rahul" — object was shared!\n\nconst deep = structuredClone(users); // fully independent` }]} defaultLanguage="javascript" id="arr-deep" />
  </section>

  {/* 17. Mutable vs immutable */}
  <section className="mb-12">
   <SectionHeading n={17}>Mutable vs Immutable Operations</SectionHeading>
   <Para>Some methods change the original array; others return a new one. This distinction is critical in React, Redux, and state management.</Para>
   <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-4">
     <div className="text-sm font-bold text-red-700 dark:text-red-300 mb-2">Mutating (changes original)</div>
     <div className="flex flex-wrap gap-1.5">{['push', 'pop', 'shift', 'unshift', 'splice', 'sort', 'reverse'].map((m) => <Pill key={m}>{m}()</Pill>)}</div>
    </div>
    <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 p-4">
     <div className="text-sm font-bold text-green-700 dark:text-green-300 mb-2">Immutable (returns new array)</div>
     <div className="flex flex-wrap gap-1.5">{['slice', 'concat', 'map', 'filter'].map((m) => <Pill key={m}>{m}()</Pill>)}</div>
    </div>
   </div>
  </section>

  {/* 18. splice vs slice */}
  <section className="mb-12">
   <SectionHeading n={18}>splice() vs slice()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const nums = [1, 2, 3, 4];\n\n// slice -> COPY, original unchanged\nconst part = nums.slice(1, 3); // [2, 3]   nums still [1,2,3,4]\n\n// splice -> CUT, mutates original\nnums.splice(1, 2);             // removes 2 items -> nums is [1, 4]` }]} defaultLanguage="javascript" id="arr-splice" />
   <Callout tone="tip" title="Memory trick"><Code>slice</Code> = copy. <Code>splice</Code> = cut.</Callout>
  </section>

  {/* 19. Across languages */}
  <section className="mb-12">
   <SectionHeading n={19}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `const products: string[] = ["Laptop", "Phone"];\nproducts.push("Watch");` }]} defaultLanguage="typescript" id="arr-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `import java.util.*;\n\nList<String> fruits = new ArrayList<>();\nfruits.add("Apple");\nfruits.add("Banana");\nSystem.out.println(fruits);` }]} defaultLanguage="java" id="arr-java" />
   <Callout tone="info">JavaScript arrays are dynamic and can hold mixed types; Java&apos;s fixed-size arrays differ from its resizable <Code>ArrayList</Code>.</Callout>
  </section>

  {/* 20. Real world */}
  <section className="mb-12">
   <SectionHeading n={20}>Real-World Example</SectionHeading>
   <Para>Summing an order&apos;s item prices — an array of objects in action:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const order = {\n  id: 101,\n  items: [\n    { name: "Pizza", price: 400 },\n    { name: "Burger", price: 250 },\n  ],\n};\n\nlet total = 0;\nfor (const item of order.items) {\n  total += item.price;\n}\nconsole.log(total);` }]} defaultLanguage="javascript" id="arr-realworld" />
   <Output>650</Output>
  </section>

  {/* 21. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={21}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Assuming arrays copy by value">Assignment shares the same array reference.</Callout>
   <Callout tone="warning" title="shift/unshift in hot paths">They&apos;re O(n); prefer push/pop where order allows.</Callout>
   <Callout tone="warning" title="Confusing splice and slice">slice copies; splice mutates.</Callout>
   <Callout tone="warning" title="Forgetting arrays are objects">Use <Code>Array.isArray()</Code>, not <Code>typeof</Code>.</Callout>
   <Callout tone="warning" title="Creating sparse arrays">Assigning past the end leaves holes that some methods skip.</Callout>
  </section>

  {/* 22. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={22}>Real Interview Questions</SectionHeading>
   <QA q="Why is array access O(1) but search O(n)?">
    Access computes a memory offset from the index directly. Search has no index to jump to, so it may scan every element until it finds a match.
   </QA>
   <QA q={<>Why is <Code>unshift()</Code> O(n) but <Code>push()</Code> O(1)?</>}>
    <Code>unshift</Code> inserts at the front, so every existing element must shift one index to the right. <Code>push</Code> adds at the end and touches nothing else.
   </QA>
   <QA q={<>Difference between <Code>splice</Code> and <Code>slice</Code>?</>}>
    <Code>slice(start, end)</Code> returns a copy and leaves the original intact. <Code>splice(start, count)</Code> mutates the original by removing/inserting elements.
   </QA>
   <QA q="Are arrays objects in JavaScript?">
    Yes — arrays are specialized objects with numeric string keys and a <Code>length</Code> property. That&apos;s why <Code>typeof [] === "object"</Code> and you need <Code>Array.isArray()</Code>.
   </QA>
   <QA q="What is a sparse array?">
    An array with holes — indexes that were never assigned (e.g. after <Code>arr[5] = "x"</Code> on an empty array). <Code>length</Code> grows but the gaps aren&apos;t real <Code>undefined</Code> values.
   </QA>
   <QA q="How do you deep-copy an array of objects?">
    <Code>structuredClone(arr)</Code>. A spread or <Code>slice()</Code> is shallow — the nested objects stay shared.
   </QA>
  </section>

  {/* 23. Mental model */}
  <section className="mb-12">
   <SectionHeading n={23}>Mental Model</SectionHeading>
   <Para>Think of an array as a <Mark>numbered locker system</Mark>. Knowing the locker number gives instant access — which is exactly why index access is <Code>O(1)</Code>.</Para>
  </section>

  {/* 24 & 25 */}
  <section className="mb-4">
   <SectionHeading n={24}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Arrays are ordered collections that provide O(1) index-based access and form the foundation for handling collections of data in JavaScript.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Understanding indexing, complexity, references, copying behavior, and mutation vs immutability is essential before Array Methods, functional programming, React, and interview problems. Most production code spends much of its time processing arrays of objects.
   </Callout>
  </section>
 </div>
);

export default JSArrays;
